import React, { useEffect, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { Mail } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { scrollState, clamp } from '@/lib/depth';

/**
 * Partnerships — a fixed overlay composed around hero3 (the clouds), layering in
 * with the same motion language as the home text once the cliff (hero2) has
 * swept off-frame and only hero3 remains. Not a scrolling depth card.
 */
const PARTNERS = ['Samsung', 'Meta', 'PlayStation', 'Nvidia', 'Armani', 'Nespresso', 'Best Buy', 'Seedance 2.0', 'Coinbase'];

const IN_START = 4300; // the cliff is gone — only hero3 remains
const OUT_START = 5500; // hold longer — dedicated space so it isn't easy to pass
const OUT_END = 5900;

const EASE = [0.22, 1, 0.36, 1];

export const PartnershipsOverlay = () => {
  const ref = useRef(null);
  const [active, setActive] = useState(false);

  useEffect(() => {
    let raf;
    const tick = () => {
      const cam = scrollState.camera;
      const exit = clamp(1 - (cam - OUT_START) / (OUT_END - OUT_START), 0, 1);
      const shown = cam > IN_START;
      const op = shown ? exit : 0;
      if (ref.current) {
        ref.current.style.opacity = op.toFixed(3);
        ref.current.style.visibility = op < 0.002 ? 'hidden' : 'visible';
      }
      if (shown && !active) setActive(true);
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [active]);

  return (
    <div
      ref={ref}
      className="fixed inset-0 z-[8] flex items-center justify-start pointer-events-none"
      style={{ opacity: 0, visibility: 'hidden', transition: 'opacity 0.45s ease' }}
      aria-hidden="true"
      data-testid="partnerships-overlay"
    >
      <motion.div
        className="pointer-events-auto flex max-w-[46vw] flex-col items-center pl-[9vw] pr-6 text-center"
        style={{ textShadow: '0 2px 36px rgba(0,0,0,0.92)', transformPerspective: 1200, willChange: 'transform' }}
        initial={{ z: -130 }}
        animate={active ? { z: 0 } : { z: -130 }}
        transition={{ duration: 1.2, ease: EASE, delay: 0.05 }}
      >
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={active ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
          transition={{ duration: 0.9, ease: EASE, delay: 0.05 }}
          className="font-mono-label text-[10px] uppercase tracking-[0.42em] text-primary/70"
        >
          Partnerships
        </motion.p>
        <motion.h2
          initial={{ opacity: 0, y: 28 }}
          animate={active ? { opacity: 1, y: 0 } : { opacity: 0, y: 28 }}
          transition={{ duration: 1.1, ease: EASE, delay: 0.15 }}
          className="mt-5 font-display text-4xl sm:text-[3.4rem] text-cream leading-[1.04]"
        >
          Let’s build something together.
        </motion.h2>
        <motion.div
          initial={{ scaleX: 0, opacity: 0 }}
          animate={active ? { scaleX: 1, opacity: 1 } : { scaleX: 0, opacity: 0 }}
          transition={{ duration: 0.9, delay: 0.5, ease: EASE }}
          className="mt-7 h-px w-20 bg-gradient-to-r from-transparent via-cream/45 to-transparent"
        />
        <motion.p
          initial={{ opacity: 0 }}
          animate={active ? { opacity: 1 } : { opacity: 0 }}
          transition={{ duration: 1.2, delay: 0.6 }}
          className="mt-7 max-w-[30rem] text-base sm:text-lg leading-relaxed text-cream/80"
        >
          Brand partnerships, campaigns, and collaborations — across tech, AI, and gaming.
        </motion.p>
        <motion.p
          initial={{ opacity: 0 }}
          animate={active ? { opacity: 1 } : { opacity: 0 }}
          transition={{ duration: 1, delay: 0.85 }}
          className="mt-8 max-w-[34rem] font-mono-label text-[10px] sm:text-[11px] uppercase leading-[2] tracking-[0.22em] text-cream/55"
        >
          {PARTNERS.join('   ·   ')}
        </motion.p>
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={active ? { opacity: 1, y: 0 } : { opacity: 0, y: 16 }}
          transition={{ duration: 1, delay: 1.05, ease: EASE }}
          className="mt-9 flex justify-center"
        >
          <Button asChild variant="cream" size="pill" data-testid="work-email-btn">
            <a href="mailto:joe@rakugomedia.com">
              <Mail />
              joe@rakugomedia.com
            </a>
          </Button>
        </motion.div>
      </motion.div>
    </div>
  );
};

export default PartnershipsOverlay;
